/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import { TraceState as ApiTraceState, TextMapPropagator, Context, TraceFlags, TextMapSetter, TextMapGetter, trace } from "@opentelemetry/api";
import { W3CTraceContextPropagator, TraceState } from "@opentelemetry/core";

import { getConfiguration } from "../configuration/StartupConfig";
import { setFw4Tag, getFw4Tag } from "../export/SpanEmbedder";
import { markSpanPropagatedNow } from "../export/SpanUpdater";
import { ComponentLogger } from "../Logger";
import { CTIds } from "../Types";
import { Fw4Tag } from "./Fw4Tag";
import { verboseFormatException } from "../utils/LoggingUtils";
import { setXDtc, setServerId, setMobileTag } from "../utils/context/RumLite";
import * as XDtcHeader from "./XDtcHeader";
import * as MobileTag from "./MobileTag";

// ============================================================================

export const X_DYNTATRACE_HEADER = "x-dynatrace";
export const X_DTC_HEADER = "x-dtc";

// some shortcuts
const { getSpanContext, getSpan, isSpanContextValid, setSpanContext } = trace;

// ----------------------------------------------------------------------------
export class DtTextMapPropagator implements TextMapPropagator {
	constructor(config?: Partial<CTIds>) {
		config = { ...getConfiguration(), ...config };
		if (config == null || typeof(config) !== "object") {
			throw new TypeError("DtTextMapPropagator: config invalid");
		}
		if (typeof(config.clusterId) !== "number") {
			throw new TypeError("DtTextMapPropagator: clusterId must be a number");
		}
		if (typeof(config.tenantId) !== "number") {
			throw new TypeError("DtTextMapPropagator: tenantId must be a number");
		}
		this._ids = {
			clusterId: config.clusterId,
			tenantId: config.tenantId
		};
		this._traceStateKey = Fw4Tag.toTraceStateKey(this._ids);
	}

	// ------------------------------------------------------------------------
	public inject(context: Context, carrier: unknown, setter: TextMapSetter): void {
		const span = getSpan(context);
		if (span == null) {
			this._logger.warn("inject: sensor attempted to inject context missing a Span");
			return;
		}
		if (!isSpanContextValid(span.spanContext())) {
			this._logger.info("inject: sensor attempted to inject an invalid span context");
			return;
		}

		// Set last-propagation-time
		markSpanPropagatedNow(span);

		// update TraceState by either replacing our @dt entry or add a new one
		const spanContext = span.spanContext();
		let parentTag = getFw4Tag(spanContext.traceState);
		if (parentTag == null) {
			if (this._logger.debugEnabled) {
				this._logger.debug("inject: create FW4 tag");
			}
			// Usually we should have a tag on TraceState, if there is none create one
			parentTag = new Fw4Tag(this._ids).setRootPathRandom();
		}

		const tag = parentTag.propagate(spanContext);
		const xDt = tag.toXDt();
		setter.set(carrier, X_DYNTATRACE_HEADER, xDt);
		// Update TraceState in a new SpanContext to avoid side effects
		const injectSpanContext = { ...spanContext };
		if (injectSpanContext.traceState == null) {
			injectSpanContext.traceState = new TraceState();
		}
		injectSpanContext.traceState = injectSpanContext.traceState.set(this._traceStateKey, tag.toTraceStateValue());
		this._w3cTraceContext.inject(setSpanContext(context, injectSpanContext), carrier, setter);

		if (this._logger.debugEnabled) {
			this._logger.debug(`inject: x-dt: ${xDt} - traceId: ${injectSpanContext.traceId}, spanId: ${injectSpanContext.spanId}, traceState: ${injectSpanContext.traceState.serialize()}`);
		}
	}

	// ------------------------------------------------------------------------
	public extract(parentContext: Context, carrier: unknown, getter: TextMapGetter): Context {
		// first extract W3C headers
		let context = this._w3cTraceContext.extract(parentContext, carrier, getter);

		if (this._logger.debugEnabled) {
			const spanContext = getSpanContext(context);
			this._logger.debug(`extract: W3C traceId: ${spanContext?.traceId}, spanId: ${spanContext?.spanId}`);
		}

		// check x-dynatrace header for FW4 tag, if present use it
		const xDynaTrace = this._getXdynaTrace(carrier, getter);
		let fw4Tag = this._getFw4FromXdynaTrace(xDynaTrace);
		if (fw4Tag != null) {
			context = this._updateContextFromXDt(fw4Tag, context);
		} else if (context !== parentContext) {
			// parse FW4 tag from TraceState if present and attach it for later use
			const spanContext = getSpanContext(context);
			if (spanContext?.traceState != null) {
				fw4Tag = this._tagFromTraceState(spanContext.traceState);
				if (fw4Tag != null) {
					setFw4Tag(spanContext.traceState, fw4Tag);
				}
			}
		}

		if (fw4Tag == null) {
			let serverId: number | undefined;
			// check for Mobile tag which may hold serverId
			if (xDynaTrace != null) {
				serverId = MobileTag.getServerId(xDynaTrace);
				// only non zero serverIds are valid
				if (serverId) {
					if (this._logger.debugEnabled) {
						this._logger.debug(`extract: use MobileTag: ${xDynaTrace}, serverId: ${serverId}`);
					}
					context = setServerId(context, serverId);
					context = setMobileTag(context, xDynaTrace);
				} else {
					this._logger.warn(`extract: Failed to parse serverId from mobile tag ${xDynaTrace}`);
				}
			}

			if (!serverId) {
				// with no Fw4 nor MT tag, server id may come from x-dtc
				const xDtc = this._getXdtc(carrier, getter);
				if (xDtc != null) {
					serverId = XDtcHeader.getServerId(xDtc);
					// only non zero serverIds are valid
					if (serverId) {
						if (this._logger.debugEnabled) {
							this._logger.debug(`extract: use x-dtc: ${xDtc}, serverId: ${serverId}`);
						}
						context = setServerId(context, serverId);
						context = setXDtc(context, xDtc);
					} else {
						this._logger.warn(`extract: Failed to parse serverId from x-dtc header ${xDtc}`);
					}
				}
			}
		} else {
			this._updateTraceFlags(fw4Tag, context);
		}

		if (this._logger.debugEnabled) {
			const spanContext = getSpanContext(context);
			if (spanContext == null) {
				this._logger.debug("extract: <no parent>");
			} else {
				this._logger.debug(`extract: traceId: ${spanContext.traceId}, spanId: ${spanContext.spanId}, traceState: ${spanContext.traceState != null ? spanContext.traceState.serialize() : "<>"}`);
			}
		}

		return context;
	}

	// ------------------------------------------------------------------------
	fields(): string[] {
		return [X_DYNTATRACE_HEADER, ...this._w3cTraceContext.fields()];
	}

	// ------------------------------------------------------------------------
	private _tagFromTraceState(traceState: ApiTraceState): Fw4Tag | undefined {
		const traceStateVal = traceState.get(this._traceStateKey);
		if (traceStateVal != null) {
			try {
				if (this._logger.debugEnabled) {
					this._logger.debug(`extract: use tracestate: ${traceStateVal}`);
				}
				return Fw4Tag.fromTraceState(this._ids, traceStateVal);
			} catch (e) {
				if (this._logger.debugEnabled) {
					this._logger.debug(`extract: failed to decode tracestate ${verboseFormatException(e)}`);
				}
			}
		}
		return undefined;
	}

	// ------------------------------------------------------------------------
	private _getFw4FromXdynaTrace(xDynatrace: string | undefined): Fw4Tag | undefined {
		if (xDynatrace != null) {
			try {
				if (this._logger.debugEnabled) {
					this._logger.debug(`extract: found x-dynatrace: ${xDynatrace}`);
				}
				const fw4Tag = Fw4Tag.fromXDt(this._ids, xDynatrace);
				if (fw4Tag.extensions.spanId && fw4Tag.extensions.traceId) {
					// only use x-dt tags with traceId and SpanId
					return fw4Tag;
				}
			} catch (e) {
				if (this._logger.debugEnabled) {
					this._logger.debug(`extract: failed to decode x-dynatrace ${verboseFormatException(e)}`);
				}
			}
		}
		return undefined;
	}

	// ------------------------------------------------------------------------
	private _getXdynaTrace(carrier: unknown, getter: TextMapGetter): string | undefined {
		const xDynaTrace = getter.get(carrier, X_DYNTATRACE_HEADER);
		return typeof(xDynaTrace) === "string" ? xDynaTrace : undefined;
	}

	// ------------------------------------------------------------------------
	private _getXdtc(carrier: unknown, getter: TextMapGetter): string | undefined {
		const xDtc = getter.get(carrier, X_DTC_HEADER);
		return typeof(xDtc) === "string" ? xDtc : undefined;
	}

	// ------------------------------------------------------------------------
	private _updateContextFromXDt(fw4Tag: Fw4Tag, context: Context): Context {
		let spanContext = getSpanContext(context);

		// if traceId in traceparent differs from FW4 tag we have to handle this
		// like a new root and prune all tracestate entries
		if ((spanContext == null) || (spanContext.traceId !== fw4Tag.extensions.traceId)) {
			if ((spanContext != null) && this._logger.debugEnabled) {
				this._logger.debug(`extract: traceId mismatch, spanContext: ${spanContext.traceId}, fw4Tag: ${fw4Tag.extensions.traceId}`);
			}
			// create a remote span context using the parsed values from fw4 tag
			spanContext = {
				traceId: fw4Tag.extensions.traceId,
				spanId: fw4Tag.extensions.spanId,
				traceFlags: fw4Tag.isIgnored ? TraceFlags.NONE : TraceFlags.SAMPLED,
				isRemote: true,
				traceState: new TraceState()
			};
			context = setSpanContext(context, spanContext);
		} else {
			if (spanContext.traceState == null) {
				spanContext.traceState = new TraceState();
			}
		}
		// add/overwrite our entry in traceState
		spanContext.traceState = spanContext.traceState!.set(this._traceStateKey, fw4Tag.toTraceStateValue());

		// attach FW4 tag for later use
		setFw4Tag(spanContext.traceState, fw4Tag);
		return context;
	}

	// ------------------------------------------------------------------------
	private _updateTraceFlags(fw4Tag: Fw4Tag, context: Context) {
		const spanContext = getSpanContext(context);
		if (spanContext == null) {
			return;
		}

		let traceFlags = spanContext.traceFlags;
		if (fw4Tag.isIgnored) {
			// eslint-disable-next-line no-bitwise
			traceFlags &= ~TraceFlags.SAMPLED;
		} else {
			// eslint-disable-next-line no-bitwise
			traceFlags |= TraceFlags.SAMPLED;
		}
		spanContext.traceFlags = traceFlags;
	}

	private readonly _ids: CTIds;
	private readonly _w3cTraceContext = new W3CTraceContextPropagator();
	private readonly _traceStateKey: string;
	private readonly _logger = new ComponentLogger("Propagator");
}
